// Einteilung — Shared Konstanten und Helper-Funktionen
// Wird von MatrixCell, AssignmentDialog, MagnetboardView, JahresansichtView, MatrixView importiert.

// Hilfsfunktion: Extrahiert die Fahrzeugnummer aus dem Funkrufnamen
// z.B. "RTW 4600/1" → "1", "RTW 4600/2" → "2", "LHF 4600" → ""
export const extractFahrzeugNummer = (funkrufname) => {
  if (!funkrufname) return '';
  const match = funkrufname.match(/[\/\-](\d{1,2})$/);
  if (match) return match[1];
  return '';
};

// Deutsche Wochentage Mapping (falls Backend englische Abkürzungen sendet)
export const WOCHENTAGE_DE = {
  'Mon': 'Mo', 'Tue': 'Di', 'Wed': 'Mi', 'Thu': 'Do', 'Fri': 'Fr', 'Sat': 'Sa', 'Sun': 'So',
  'Mo': 'Mo', 'Di': 'Di', 'Mi': 'Mi', 'Do': 'Do', 'Fr': 'Fr', 'Sa': 'Sa', 'So': 'So',
  'Monday': 'Mo', 'Tuesday': 'Di', 'Wednesday': 'Mi', 'Thursday': 'Do', 'Friday': 'Fr', 'Saturday': 'Sa', 'Sunday': 'So',
  'Montag': 'Mo', 'Dienstag': 'Di', 'Mittwoch': 'Mi', 'Donnerstag': 'Do', 'Freitag': 'Fr', 'Samstag': 'Sa', 'Sonntag': 'So',
};

export const getWochentagDE = (tag) => WOCHENTAGE_DE[tag] || tag;

// Fahrzeugbilder — re-export aus zentraler Lib für Konsistenz mit Dashboard/Fahrzeuge-Seite
import { FAHRZEUG_BILDER } from '../../lib/fahrzeugBilder';
export const vehicleImages = FAHRZEUG_BILDER;

export const STATUS_CODES = {
  K:  { label: 'Krank',       displayCode: 'K',  color: 'bg-red-500',     textColor: 'text-red-700',     bgLight: 'bg-red-100',     border: 'border-red-300' },
  SU: { label: 'Urlaub',      displayCode: 'U',  color: 'bg-cyan-500',    textColor: 'text-cyan-700',    bgLight: 'bg-cyan-100',    border: 'border-cyan-300' },
  F:  { label: 'Fortbildung', displayCode: 'F',  color: 'bg-orange-500',  textColor: 'text-orange-700',  bgLight: 'bg-orange-100',  border: 'border-orange-300' },
  WA: { label: 'Wandern',     displayCode: 'WA', color: 'bg-pink-500',    textColor: 'text-pink-700',    bgLight: 'bg-pink-100',    border: 'border-pink-300' },
  RW: { label: 'RTW Wandern', displayCode: 'RW', color: 'bg-rose-500',    textColor: 'text-rose-800',    bgLight: 'bg-rose-200',    border: 'border-rose-400' },
  T:  { label: 'Tausch',      displayCode: 'T',  color: 'bg-violet-500',  textColor: 'text-violet-700',  bgLight: 'bg-violet-100',  border: 'border-violet-300' },
  ZBV:{ label: 'ZBV',         displayCode: 'ZBV',color: 'bg-slate-500',   textColor: 'text-slate-700',   bgLight: 'bg-slate-200',   border: 'border-slate-400' },
  '--': { label: 'Frei',      displayCode: '--', color: 'bg-gray-400',    textColor: 'text-gray-600',    bgLight: 'bg-gray-100',    border: 'border-gray-300' },
};

// Farbpaletten pro Fahrzeugtyp (für unterschiedliche Funktionsfarben gleichen Funktionen)
export const FAHRZEUGTYP_FARBEN = {
  'LHF': { primary: 'bg-purple-600 hover:bg-purple-700', secondary: 'bg-blue-600 hover:bg-blue-700', tertiary: 'bg-red-600 hover:bg-red-700', quaternary: 'bg-green-600 hover:bg-green-700' },
  'HLF': { primary: 'bg-purple-600 hover:bg-purple-700', secondary: 'bg-blue-600 hover:bg-blue-700', tertiary: 'bg-red-600 hover:bg-red-700', quaternary: 'bg-green-600 hover:bg-green-700' },
  'RTW': { primary: 'bg-emerald-600 hover:bg-emerald-700', secondary: 'bg-teal-600 hover:bg-teal-700' },
  'DLK': { primary: 'bg-orange-600 hover:bg-orange-700', secondary: 'bg-amber-600 hover:bg-amber-700' },
  'TLF': { primary: 'bg-rose-600 hover:bg-rose-700', secondary: 'bg-red-600 hover:bg-red-700', tertiary: 'bg-pink-600 hover:bg-pink-700' },
  'GW': { primary: 'bg-slate-600 hover:bg-slate-700', secondary: 'bg-zinc-600 hover:bg-zinc-700' },
  'NEF': { primary: 'bg-yellow-500 hover:bg-yellow-600', secondary: 'bg-amber-500 hover:bg-amber-600' },
  'ELW': { primary: 'bg-gray-600 hover:bg-gray-700', secondary: 'bg-gray-500 hover:bg-gray-600' },
  'default': { primary: 'bg-indigo-600 hover:bg-indigo-700', secondary: 'bg-indigo-500 hover:bg-indigo-600' },
};

export const getFunktionFarbe = (fahrzeugTypCode, funktionIndex) => {
  const typCode = fahrzeugTypCode?.match(/^([A-Z]+)/)?.[1] || 'default';
  const farben = FAHRZEUGTYP_FARBEN[typCode] || FAHRZEUGTYP_FARBEN['default'];
  const colorKeys = ['primary', 'secondary', 'tertiary', 'quaternary'];
  const colorKey = colorKeys[funktionIndex % colorKeys.length];
  return farben[colorKey] || farben.primary;
};

export const extractFahrzeugTyp = (code) => {
  if (!code) return 'LHF';
  const match = code.match(/^([A-Z]+)/);
  return match ? match[1] : 'LHF';
};

export const countFahrzeugeByTyp = (fahrzeuge, typCode) => {
  if (!fahrzeuge || !typCode) return 1;
  const prefix = typCode.match(/^([A-Z]+)/)?.[1];
  if (!prefix) return 1;
  return fahrzeuge.filter(fz => {
    const fzTypCode = fz.fahrzeugtyp_code || extractFahrzeugTyp(fz.funkrufname);
    const fzPrefix = fzTypCode?.match(/^([A-Z]+)/)?.[1];
    return fzPrefix === prefix;
  }).length;
};

// Fahrzeugspezifische Abkürzung (z.B. MA1, StF2, ATF3)
// Wird nur dann mit Zahl angezeigt, wenn die Abteilung MEHR ALS EIN Fahrzeug
// desselben Typs hat. Bei genau einem Fahrzeug pro Typ wird das Kürzel ohne Zahl
// ausgegeben.
export const getFahrzeugspezifischeAbkuerzung = (funktionKuerzel, fahrzeugNummer) => {
  if (!fahrzeugNummer) return funktionKuerzel;
  return `${funktionKuerzel}${fahrzeugNummer}`;
};

/**
 * Berechnet die anzuzeigende Nummer eines Fahrzeugs innerhalb seiner Typ-Gruppe.
 * Beispiel: Wache hat LHF 4600/1, LHF 4600/2 → LHF 4600/1 erhält "1", LHF 4600/2 erhält "2".
 * Bei nur einem Fahrzeug pro Typ → leerer String (= keine Zahl anzeigen).
 *
 * @param {Array} alleFahrzeuge - Liste aller Fahrzeuge der Abteilung
 * @param {Object} aktuellesFahrzeug - Das Fahrzeug, für das die Nummer berechnet wird
 * @returns {string} '' wenn nur ein Fahrzeug des Typs, sonst die fortlaufende Nummer (1-based, sortiert)
 */
export const berechneFahrzeugNummerInTyp = (alleFahrzeuge, aktuellesFahrzeug) => {
  if (!aktuellesFahrzeug || !alleFahrzeuge) return '';
  const typCode = aktuellesFahrzeug.fahrzeugtyp_code
    || extractFahrzeugTyp(aktuellesFahrzeug.funkrufname);
  if (!typCode) return '';

  const gleicherTyp = alleFahrzeuge.filter(fz => {
    const fzTyp = fz.fahrzeugtyp_code || extractFahrzeugTyp(fz.funkrufname);
    return fzTyp === typCode;
  });

  // Nur 1 Fahrzeug dieses Typs → keine Nummer
  if (gleicherTyp.length <= 1) return '';

  // Stabile Sortierung nach funkrufname / id, dann Index ermitteln
  gleicherTyp.sort((a, b) => (a.funkrufname || '').localeCompare(b.funkrufname || ''));
  const idx = gleicherTyp.findIndex(fz => fz.id === aktuellesFahrzeug.id);
  return idx >= 0 ? String(idx + 1) : '';
};

// Funktions-Farben für Matrix-Zellen (kompakte einfarbige Variante) — FALLBACK
// Wird nur noch verwendet, wenn weder Fahrzeug-Farbe noch Typ-Default greift.
export const FUNKTIONS_FARBEN = {
  'StF': { bg: 'bg-purple-500', text: 'text-white' },
  'Ma': { bg: 'bg-blue-600', text: 'text-white' },
  'FF': { bg: 'bg-orange-500', text: 'text-white' },
  'ATF': { bg: 'bg-red-600', text: 'text-white' },
  'ATM': { bg: 'bg-red-400', text: 'text-white' },
  'WTF': { bg: 'bg-green-600', text: 'text-white' },
  'WTM': { bg: 'bg-green-400', text: 'text-white' },
  'TrF': { bg: 'bg-rose-600', text: 'text-white' },
  'TrM': { bg: 'bg-rose-500', text: 'text-white' },
  'MVE': { bg: 'bg-teal-600', text: 'text-white' },
  'NEF': { bg: 'bg-yellow-500', text: 'text-black' },
  // ZUS = Zusatzkraft / Nur Mitfahrer (Praktikant ohne Funktion, zählt NICHT zur Stärke)
  'ZUS': { bg: 'bg-amber-200 border border-amber-500 border-dashed', text: 'text-amber-900' },
  'default': { bg: 'bg-slate-500', text: 'text-white' },
};

// =====================================================================
// FAHRZEUG-FARBEN (Magnetboard-Stil): Farbe wird primär aus
// `fahrzeug.magnetboard_farbe` (Hex) genommen, sonst Default nach Typ.
// =====================================================================

// Default-Hex-Farben pro Fahrzeugtyp (zurückhaltend, nicht zu bunt)
export const FAHRZEUGTYP_HEX_FARBEN = {
  LHF:        '#1d4ed8', // Blau
  HLF:        '#1d4ed8', // Blau
  RTW:        '#dc2626', // Rot
  NEF:        '#eab308', // Gelb
  DLK:        '#16a34a', // Grün
  ELW:        '#475569', // Slate
  KdoW:       '#0f172a', // Schwarz/Slate-900
  GW:         '#7c3aed', // Lila
  'GW-HYGIENE': '#7c3aed',
  TLF:        '#0891b2', // Cyan
  RW:         '#b45309', // Amber-700
  MTF:        '#64748b', // Slate-500
  KLEF:       '#64748b',
  SONSTIGE:   '#475569',
  DEFAULT:    '#475569',
};

// Liefert die Hex-Farbe für ein Fahrzeug-Objekt.
// Reihenfolge: 1) magnetboard_farbe explizit gesetzt 2) Default nach Typ 3) DEFAULT
export const getFahrzeugFarbe = (fahrzeug) => {
  if (!fahrzeug) return FAHRZEUGTYP_HEX_FARBEN.DEFAULT;
  if (fahrzeug.magnetboard_farbe) return fahrzeug.magnetboard_farbe;
  const code = (fahrzeug.fahrzeugtyp_code || fahrzeug.typ || '').toUpperCase();
  // Erstes Wort des Codes nehmen (z.B. "RTW-STOL" → "RTW")
  const head = code.split(/[-\s]/)[0];
  return FAHRZEUGTYP_HEX_FARBEN[code] || FAHRZEUGTYP_HEX_FARBEN[head] || FAHRZEUGTYP_HEX_FARBEN.DEFAULT;
};

// Liest aus einem Hex die WCAG-konforme Textfarbe (schwarz oder weiß).
export const getKontrastFarbe = (hex) => {
  if (!hex || !hex.startsWith('#')) return '#ffffff';
  try {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    // Luminanz nach W3C
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? '#0f172a' : '#ffffff';
  } catch {
    return '#ffffff';
  }
};

// Qualifikations-Kürzel für Mobile/Compact-Anzeige
export const QUALIFIKATION_KURZ = {
  'Notfallsanitäter': 'NotSan',
  'Rettungssanitäter': 'RettSan',
  'Rettungsassistent': 'RettAss',
  'Notsan': 'NotSan',
  'Rettsan': 'RettSan',
  'RS': 'RS',
  'RDV': 'RDV',
  'Grundausbildung': 'Grundausb.',
};
